/**
 * Secondary 1-4 hub page content (stream-selector landing pages).
 *
 * Extracted from the former monolithic `buildLevelsContent` so the four Sec hub
 * page chunks pull only the hub literals. Shared by Sec1..4Page and composed by
 * the `buildLevelsContent` barrel.
 */
import { levelPageIds } from './pageIds';
import type { LevelPageContent } from './types';

export function buildSecondaryHubContent(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const hubs: Record<string, LevelPageContent> = {
    sec1: {
      id: levelPageIds.sec1,
      slug: '/sec-1',
      seo: {
        title: `Secondary 1 Math Tuition (G1/G2/G3/IP) | ${brandName}`,
        description:
          'Secondary 1 Math tuition for Full SBB cohorts: G1, G2, G3, and IP. Build strong foundations for the SEC (Secondary Education Certificate).',
      },
      hero: {
        eyebrow: 'SECONDARY 1 MATHEMATICS',
        headline:
          'Build strong foundations during the transition from primary school.',
        subhead:
          'Sec 1 is where students begin developing algebraic thinking and problem-solving skills. Under Full SBB, students will sit the Singapore–Cambridge Secondary Education Certificate (SEC), with each subject taken at G1, G2 or G3 level. DeepThink supports all levels: G1, G2, G3, and IP.',
        ctaLabel: 'Book a free trial class',
      },
      streamSelector: {
        title: 'Choose your Mathematics level',
        streams: [
          {
            id: 'g1',
            label: 'G1',
            path: '/sec-1-g1',
            description:
              'SEC at G1 level (aligned to previous N(T) standard). Foundation Mathematics with emphasis on building confidence and core skills.',
          },
          {
            id: 'g2',
            label: 'G2',
            path: '/sec-1-g2',
            description:
              'SEC at G2 level (aligned to previous N(A) standard). Standard Mathematics balancing conceptual understanding with practical application.',
          },
          {
            id: 'g3',
            label: 'G3',
            path: '/sec-1-g3',
            description:
              'SEC at G3 level (aligned to O-Level standard). Advanced Mathematics with deeper exploration of concepts and problem-solving.',
          },
          {
            id: 'ip',
            label: 'IP Programme',
            path: '/sec-1-ip',
            description:
              'Accelerated curriculum for Integrated Programme students preparing for A-Level Mathematics.',
          },
        ],
      },
      syllabus: {
        title: 'What we cover',
        eyebrow: 'Syllabus',
        body: 'Under Full SBB, all students sit the Singapore–Cambridge Secondary Education Certificate (SEC); there is no separate O-Level or N-Level. Each subject is offered at G1, G2 or G3 level (students can mix levels across subjects). The MOE Lower Secondary syllabus is level-specific. In Sec 1 we introduce the relevant content for your child’s Mathematics level at a pace that respects the transition from primary. Choose your level above to see the full syllabus.',
        topics: [
          {
            category: 'G1 (SEC at G1 level)',
            items: [
              'G1 is aligned to the previous Normal (Technical) standard. Sec 1 covers number, four operations, ratio, percentage, algebra, angles, statistics, symmetry, area and perimeter, and surface area and volume. Select G1 above for the full topic list.',
            ],
          },
          {
            category: 'G2 (SEC at G2 level)',
            items: [
              'G2 is aligned to the previous Normal (Academic) standard. Sec 1 follows the MOE chapter-based Lower Secondary syllabus: primes and factors, integers and real numbers, algebra, linear equations, number patterns, percentage, ratio and rate, angles and triangles, perimeter and area, volume and surface area, statistics. Select G2 above for the full chapter list.',
            ],
          },
          {
            category: 'G3 (SEC at G3 level)',
            items: [
              'G3 is aligned to the previous O-Level standard. Sec 1 follows the same core areas as G2 (algebra, equations, functions, geometry, mensuration, statistics) with the exact chapters on the G3 page. Select G3 above for the full syllabus.',
            ],
          },
          {
            category: 'IP (Integrated Programme)',
            items: [
              'Accelerated path toward A-Level Mathematics. Covers the same core Lower Secondary areas at a faster pace. Select IP above for the full syllabus for your programme.',
            ],
          },
        ],
        note: 'Sec 1 is when algebra and abstract thinking take centre stage. We focus on clarity and "why it works" so students start secondary Math on solid ground. Exact topics and order are set by MOE and differ by level (G1/G2/G3).',
      },
      challenges: {
        title: 'What Sec 1 students (and parents) often tell us',
        body: 'The jump from primary to secondary is real. Here’s what we hear most:',
        items: [
          {
            title: '"Everything feels new — and Math suddenly has letters"',
            description:
              'Algebra can feel like a different subject. Students who were strong in arithmetic sometimes stall when x and y appear. That’s normal. The shift from “get the answer” to “work with unknowns” takes time and the right support.',
          },
          {
            title: 'Pace and volume that catch students off guard',
            description:
              'Secondary moves faster and covers more in a term. What used to take weeks might be covered in days. Students who never needed to study much for Math can suddenly feel behind — not because they’re “weak,” but because the game has changed.',
          },
          {
            title: 'Old gaps that start to matter',
            description:
              'Fractions, ratios, or word-problem strategies that were shaky in primary become real obstacles in Sec 1. Addressing them early prevents them from snowballing.',
          },
          {
            title: 'A new school, new expectations, new routine',
            description:
              'Sec 1 isn’t just about Math — it’s a new environment, more subjects, and higher expectations. Some students need a calm, consistent space where Math is explained clearly so they can focus on learning, not keeping up.',
          },
        ],
      },
      howWeHelp: {
        title: 'How we support Sec 1 students',
        body: 'We’re built for this exact transition: to make the first year of secondary Math clear, manageable, and confidence-building.',
        points: [
          {
            title: 'See exactly where they stand',
            description:
              'Our practice platform shows which concepts are secure and which need attention — so we don’t guess. We fill gaps from primary and build Sec 1 content on a firm base.',
          },
          {
            title: 'Introduce algebra so it makes sense',
            description:
              'Teachers connect new ideas to what students already know and emphasise *why* steps work, not just the steps. That’s how algebraic thinking sticks.',
          },
          {
            title: 'Practice that matches their needs',
            description:
              'Students spend time on what they actually need to improve, not on topics they’ve already got. That keeps Sec 1 from feeling like an endless catch-up.',
          },
          {
            title: 'A steady place to build confidence',
            description:
              'Weekly tuition gives a predictable space to ask questions, break down harder questions, and leave each session clearer than they started. By the end of Sec 1, we aim for students to feel “I can do this.”',
          },
        ],
        closingLine:
          'A strong Sec 1 sets the tone for the rest of lower secondary. We’re here to make that year count.',
      },
    },
    sec2: {
      id: levelPageIds.sec2,
      slug: '/sec-2',
      seo: {
        title: `Secondary 2 Math Tuition (G1/G2/G3/IP) | ${brandName}`,
        description:
          'Secondary 2 Math tuition for Full SBB cohorts: G1, G2, G3, and IP. Consolidate foundations for the SEC (Secondary Education Certificate).',
      },
      hero: {
        eyebrow: 'SECONDARY 2 MATHEMATICS',
        headline: 'Consolidate foundations and build towards upper secondary.',
        subhead:
          'Sec 2 deepens mathematical understanding and prepares students for the demands of Sec 3–4. Under Full SBB, students sit the SEC with each subject at G1, G2 or G3 level. DeepThink supports all levels: G1, G2, G3, and IP.',
        ctaLabel: 'Book a free trial class',
      },
      streamSelector: {
        title: 'Choose your Mathematics level',
        streams: [
          {
            id: 'g1',
            label: 'G1',
            path: '/sec-2-g1',
            description:
              'SEC at G1 level. Continue building mathematical confidence with clear, structured support.',
          },
          {
            id: 'g2',
            label: 'G2',
            path: '/sec-2-g2',
            description:
              'SEC at G2 level. Strengthen understanding and prepare for upper secondary mathematics.',
          },
          {
            id: 'g3',
            label: 'G3',
            path: '/sec-2-g3',
            description:
              'SEC at G3 level. Develop advanced problem-solving skills and mathematical reasoning.',
          },
          {
            id: 'ip',
            label: 'IP Programme',
            path: '/sec-2-ip',
            description:
              'Maintain pace with accelerated curriculum while building conceptual depth.',
          },
        ],
      },
      syllabus: {
        title: 'What we cover',
        eyebrow: 'Syllabus',
        body: 'Under Full SBB, all students sit the Singapore–Cambridge Secondary Education Certificate (SEC); there is no separate O-Level or N-Level. Each subject is offered at G1, G2 or G3 level. The MOE Lower Secondary syllabus is level-specific. Sec 2 completes and deepens the content for your child’s Mathematics level so they step into Sec 3 ready. Choose your level above to see the full syllabus.',
        topics: [
          {
            category: 'G1 (SEC at G1 level)',
            items: [
              'G1 is aligned to the previous Normal (Technical) standard. Sec 2 topics include triangles and quadrilaterals, linear algebra, equations, functions and graphs, proportion, rate and speed, congruence and similarity, Pythagoras’ theorem, data analysis, probability, prisms and cylinders. Select G1 above for the full list.',
            ],
          },
          {
            category: 'G2 (SEC at G2 level)',
            items: [
              'G2 is aligned to the previous Normal (Academic) standard. Sec 2 continues the chapter-based syllabus: linear expressions and inequalities, linear functions and graphs, simultaneous equations, expansion and factorisation, algebraic fractions, direct and inverse proportion, polygons, congruence and similarity, Pythagoras, volume and surface area of pyramids/cones/spheres, probability, statistical diagrams, averages. Select G2 above for the full chapter list.',
            ],
          },
          {
            category: 'G3 (SEC at G3 level)',
            items: [
              'G3 is aligned to the previous O-Level standard. Sec 2 follows the same broad areas as G2 with the exact chapters on the G3 page. Select G3 above for the full syllabus.',
            ],
          },
          {
            category: 'IP (Integrated Programme)',
            items: [
              'Accelerated path toward A-Level. Sec 2 completes Lower Secondary content at pace. Select IP above for the full syllabus for your programme.',
            ],
          },
        ],
        note: 'Sec 2 is the year to lock in lower secondary understanding. Strong foundations here make Upper Secondary and level-specific content much easier. Exact topics and order are set by MOE and differ by level (G1/G2/G3).',
      },
      challenges: {
        title: 'What Sec 2 students (and parents) often tell us',
        body: 'Sec 2 is when content gets heavier and the path to Sec 3 becomes real. Common concerns:',
        items: [
          {
            title: 'Content that builds on a shaky Sec 1',
            description:
              'If Sec 1 felt rushed or some topics were never fully understood, Sec 2 can feel like running on sand. Algebra, geometry, and problem-solving pile on — and gaps from last year start to show up in tests.',
          },
          {
            title: 'Harder questions, not just more topics',
            description:
              'Sec 2 doesn’t only add new material; it demands deeper application. Multi-step problems and questions that combine several ideas can trip students who relied on memorising steps.',
          },
          {
            title: 'Stream choices and expectations looming',
            description:
              'Families start thinking about G1, G2, G3, or IP — and whether their child is “on track.” Math often feels like the barometer. Students can feel the weight of “getting it right” for next year.',
          },
          {
            title: 'Wanting to fix it before Upper Secondary',
            description:
              'Many students and parents use Sec 2 as the year to shore up weak areas before the exam syllabus kicks in. The intention is right; the challenge is doing it in a way that doesn’t feel like constant catch-up.',
          },
        ],
      },
      howWeHelp: {
        title: 'How we support Sec 2 students',
        body: 'We help Sec 2 students consolidate what they’ve learned and close gaps so they enter Upper Secondary on solid ground.',
        points: [
          {
            title: 'Pinpoint what’s secure and what’s not',
            description:
              'Our platform clarifies exactly which concepts and question types need work. We don’t re-teach everything — we focus on the areas that will matter most for Sec 3 and beyond.',
          },
          {
            title: 'Deepen understanding, not just coverage',
            description:
              'Teachers link Sec 2 content back to Sec 1 and forward to Upper Secondary. Students learn to see connections and handle harder, combined questions with confidence.',
          },
          {
            title: 'Practice that respects their time',
            description:
              'We target weak spots and reinforce strengths efficiently. That means less busywork and more progress, so Sec 2 feels like building rather than treading water.',
          },
          {
            title: 'Leave Sec 2 ready for the next step',
            description:
              'By the end of the year, we aim for students to have a clear picture of their strengths, addressed gaps, and the confidence to take on stream-specific and exam-focused Math in Sec 3.',
          },
        ],
        closingLine:
          'Sec 2 is the bridge year. We’re here to make sure it’s strong.',
      },
    },
    sec3: {
      id: levelPageIds.sec3,
      slug: '/sec-3',
      seo: {
        title: `Secondary 3 Math Tuition (G1/G2/G3/IP) | ${brandName}`,
        description:
          'Secondary 3 Math tuition for Full SBB cohorts: G1, G2, G3, and IP. Begin focused SEC (Secondary Education Certificate) preparation.',
      },
      hero: {
        eyebrow: 'SECONDARY 3 MATHEMATICS',
        headline: 'Begin focused exam preparation with strategic teaching.',
        subhead:
          'Sec 3 marks the start of intensive preparation for the Singapore–Cambridge Secondary Education Certificate (SEC). Under Full SBB, all students sit the SEC with each subject at G1, G2 or G3 level. DeepThink helps students across all levels — G1, G2, G3, and IP — stay focused and on track.',
        ctaLabel: 'Book a free trial class',
      },
      streamSelector: {
        title: 'Choose your Mathematics level',
        streams: [
          {
            id: 'g1',
            label: 'G1',
            path: '/sec-3-g1',
            description:
              'SEC at G1 level. Prepare with clear, manageable exam-focused teaching (aligned to previous N(T) standard).',
          },
          {
            id: 'g2',
            label: 'G2',
            path: '/sec-3-g2',
            description:
              'SEC at G2 level. Build towards the exam with targeted practice (aligned to previous N(A) standard).',
          },
          {
            id: 'g3',
            label: 'G3',
            path: '/sec-3-g3',
            description:
              'SEC at G3 level. Develop exam-ready understanding for E-Math and A-Math (aligned to O-Level standard).',
          },
          {
            id: 'ip',
            label: 'IP Programme',
            path: '/sec-3-ip',
            description:
              'Continue accelerated learning while preparing for A-Level Mathematics.',
          },
        ],
      },
      syllabus: {
        title: 'What we cover',
        eyebrow: 'Syllabus',
        body: "Under Full SBB, all students sit the SEC; there is no separate O-Level or N-Level. Mathematics can be taken at G1, G2 or G3 level, and the certificate will show each subject with its level and grade. In Sec 3 we cover the first year of the Upper Secondary syllabus for your child's chosen level. Choose your level above to see the full syllabus.",
        topics: [
          {
            category: 'G1 (SEC at G1 level)',
            items: [
              'Sec 3 G1 covers indices and standard form, algebraic expansion and factorisation, arc lengths and sector areas, pyramids/cones/spheres, cumulative frequency, graphs of linear equations, and simultaneous linear equations. Select G1 above for the full topic list.',
            ],
          },
          {
            category: 'G2 (SEC at G2 level)',
            items: [
              'Sec 3 G2 covers algebraic expressions and formulae, quadratic equations and graphs, indices and standard form, coordinate geometry, graphs of functions, trigonometry, congruence and similarity. Select G2 above for the full chapter list.',
            ],
          },
          {
            category: 'G3 (SEC at G3 level)',
            items: [
              'Sec 3 G3 covers the same E-Math areas as G2 plus Additional Mathematics topics (e.g. polynomials, binomial theorem, calculus, trig identities) as per the MOE syllabus. Select G3 above for the full syllabus.',
            ],
          },
          {
            category: 'IP (Integrated Programme → A-Level)',
            items: [
              "Sec 3 IP follows the accelerated curriculum toward A-Level Mathematics. Exact topics depend on your school's programme. Select IP above for the full syllabus.",
            ],
          },
        ],
        note: 'We align practice to SEC exam formats (G1/G2/G3) from Sec 3 so good habits and exam technique are built over two years, not rushed in Sec 4. Exact topics and order are set by MOE and differ by level.',
      },
      challenges: {
        title: 'What Sec 3 students (and parents) often tell us',
        body: 'Upper Secondary starts here. The syllabus gets serious, and the SEC no longer feels far away. Common concerns:',
        items: [
          {
            title: 'A real step up in difficulty and volume',
            description:
              'New topics — trigonometry, more algebra, coordinate geometry — land quickly. Questions combine several concepts and look nothing like lower secondary. Many students need time to adjust to the pace and depth.',
          },
          {
            title: 'Old gaps that can’t be ignored anymore',
            description:
              'Weak areas from Sec 1 or 2 start to show up in Sec 3 tests. Students (and parents) realise that “catching up later” isn’t a plan — and the pressure to fix things now can feel overwhelming.',
          },
          {
            title: 'More subjects, same number of hours',
            description:
              'With other SEC subjects ramping up, Math has to compete for attention. Students need a clear plan so their Math effort is focused and effective, not scattered.',
          },
          {
            title: 'Learning how to “do” exam-style questions',
            description:
              'Knowing the content isn’t enough. Sec 3 is when students need to learn how to read questions, structure answers, and manage time — skills that take practice, not last-minute cramming.',
          },
        ],
      },
      howWeHelp: {
        title: 'How we support Sec 3 students',
        body: 'We’re built to make the first year of Upper Secondary manageable and strategic — so Sec 3 sets up a strong Sec 4.',
        points: [
          {
            title: 'Focus effort where it matters most',
            description:
              'Our platform highlights which topics and question types will have the biggest impact on exam performance. Students spend time on what actually moves the needle.',
          },
          {
            title: 'Fix weak areas before they define Sec 4',
            description:
              'We work on gaps steadily through the year with spaced practice, so Sec 4 isn’t a frantic race to cover everything. Strong Sec 3 foundations mean calmer, more confident revision later.',
          },
          {
            title: 'Build exam technique from day one',
            description:
              'Weekly tuition includes how to approach multi-step questions, manage time, and avoid common exam mistakes. By Sec 4, these habits are second nature.',
          },
          {
            title: 'Track progress so nothing is left to chance',
            description:
              'Students and parents see clearly what’s improving and what still needs work. No guessing — just a clear picture of readiness as they move toward Sec 4.',
          },
        ],
        closingLine:
          'Sec 3 is when exam preparation truly begins. We’re here to make it purposeful and productive.',
      },
    },
    sec4: {
      id: levelPageIds.sec4,
      slug: '/sec-4',
      seo: {
        title: `Secondary 4 Math Tuition (Express / NA / NT / G1 / G2 / G3 / IP) | ${brandName}`,
        description:
          'Secondary 4 Math tuition for all streams: Express, Normal Academic, Normal Technical, G1, G2, G3, and IP. O-Level and N-Level exam preparation with focused, strategic teaching.',
      },
      hero: {
        eyebrow: 'SECONDARY 4 MATHEMATICS',
        headline: 'Final year exam preparation with clarity and confidence.',
        subhead:
          'Secondary 4 demands focused revision and strategic exam preparation. DeepThink supports all streams — Express, Normal Academic (NA), Normal Technical (NT), G1, G2, G3, and IP — to ensure students perform their best in O-Level and N-Level examinations.',
        ctaLabel: 'Book a free trial class',
      },
      streamSelector: {
        title: 'Choose your stream',
        streams: [
          {
            id: 'g1',
            label: 'G1 / Normal Technical',
            path: '/sec-4-g1',
            description:
              'Final push for N(T)-Level with systematic revision and exam strategy.',
          },
          {
            id: 'g2',
            label: 'G2 / Normal Academic',
            path: '/sec-4-g2',
            description:
              'Comprehensive N(A)-Level and O-Level preparation with targeted practice.',
          },
          {
            id: 'g3',
            label: 'G3 / Express',
            path: '/sec-4-g3',
            description:
              'Excel in O-Level E-Math and A-Math with rigorous preparation.',
          },
          {
            id: 'ip',
            label: 'IP Programme',
            path: '/sec-4-ip',
            description:
              'Complete IP curriculum while building foundations for A-Level.',
          },
        ],
      },
      syllabus: {
        title: 'What we cover',
        eyebrow: 'Syllabus',
        body: "The Upper Secondary syllabus is stream-specific: G1 / Normal Technical leads to N(T)-Level, G2 / Normal Academic to N(A)-Level or O-Level E-Math, G3 / Express to O-Level E-Math and A-Math, IP to A-Level. In Sec 4 we focus on full revision and exam-style practice for your child's stream so every topic is ready to be applied under exam conditions. Choose your stream above to see the full syllabus for that track.",
        topics: [
          {
            category:
              'G1 / Normal Technical (Foundation Mathematics → N(T)-Level)',
            items: [
              'Sec 4 G1 / NT covers fractional equations, graphs of quadratic functions, quadratic equations, and trigonometry, alongside revision of all Sec 1–3 G1 content in numbers, algebra, geometry, mensuration, statistics, and probability. Select G1 above for the full list.',
            ],
          },
          {
            category:
              'G2 / Normal Academic (Standard Mathematics → N(A)-Level / O-Level E-Math)',
            items: [
              'Sec 4 G2 / NA covers applications of trigonometry, arc length and radian measure, probability of combined events, cumulative frequency, mean and standard deviation, geometrical properties of circles, and revision across numbers & algebra, geometry & measurement, and probability & statistics. Select G2 above for the full chapter list.',
            ],
          },
          {
            category: 'G3 / Express (E-Math and A-Math → O-Level)',
            items: [
              'Sec 4 G3 / Express completes the O-Level E-Math and A-Math syllabuses with revision and exam-focused practice. Select G3 above for the full syllabus and topic list.',
            ],
          },
          {
            category: 'IP (Integrated Programme → A-Level)',
            items: [
              'Sec 4 IP completes the programme curriculum and builds toward A-Level Mathematics. Exact topics depend on your school. Select IP above for the full syllabus.',
            ],
          },
        ],
        note: 'Every session is geared toward exam readiness: question selection, time management, and the clarity to perform when it counts. Exact topics and order are set by MOE and differ by stream.',
      },
      challenges: {
        title: 'What Sec 4 students (and parents) often tell us',
        body: 'This is the year it all leads to. The countdown is real. Here’s what we hear:',
        items: [
          {
            title: '“There’s so much to cover and so little time”',
            description:
              'The full syllabus, weak topics, and exam technique all demand attention. Without a clear plan, revision can feel scattered and stressful. Students need to know what to prioritise and how to use each week well.',
          },
          {
            title: 'Pressure to perform when it matters',
            description:
              'One exam, one day — and years of work feel like they’re on the line. Anxiety is normal. What helps is feeling prepared: knowing the content, having a strategy, and having practised under conditions that mimic the real thing.',
          },
          {
            title: 'Math competing with every other subject',
            description:
              'Sec 4 means revision across the board. Math can’t take all their time — so every hour has to count. Targeted practice and clear priorities make the difference between “I tried” and “I’m ready.”',
          },
          {
            title: 'Wanting to show what they actually know',
            description:
              'Many students understand the content but lose marks to careless errors, poor time allocation, or unfamiliar question phrasing. They don’t need more content cramming; they need exam craft and confidence.',
          },
        ],
      },
      howWeHelp: {
        title: 'How we support Sec 4 students',
        body: 'We’re built for the final stretch: to help students use their time well, shore up weak areas, and walk into the exam room ready to show what they know.',
        points: [
          {
            title: 'Prioritise what moves the grade',
            description:
              'Our platform shows which topics and question types will have the biggest impact on their result. We focus revision where it matters most, so effort translates into marks.',
          },
          {
            title: 'Turn weak areas into strengths — in time',
            description:
              'We don’t leave gaps to chance. We work on them with focused practice and clear explanations, so by exam day those topics feel manageable, not frightening.',
          },
          {
            title: 'Sharpen exam technique under pressure',
            description:
              'Weekly tuition includes timed practice, question selection, and how to avoid common mistakes. Students learn to stay calm and perform when it counts.',
          },
          {
            title: 'Clear visibility so nothing is left to chance',
            description:
              'Progress is tracked and visible. Students and parents know exactly where they stand — what’s secure and what still needs attention — so they can approach the exam with confidence, not hope.',
          },
        ],
        closingLine:
          'Sec 4 is the final stretch. We’re here to make sure they’re ready.',
      },
    },
  };
  return hubs[`sec${secLevel}`];
}
